import React, {useState, useEffect, useContext} from 'react';
import {Platform} from 'react-native';
import * as Keychain from 'react-native-keychain';
import {
  BUTTON_TYPES,
  KEYS,
  SEND_OTP_TYPES,
  TERMS_AND_CONDITIONS_URL,
  PRIVACY_POLICY_URL,
} from '../../utils/Constants';
import {useTheme} from '../../theme';
import {HTTP_METHODS, CREATE_USER, SEND_OTP} from '../../utils/URLConstants';
import BottomExpandableDrawer from '../../components/modules/BottomExpandableDrawer';
import {
  isEmpty,
  validPassword,
  validateEmail,
  validPhoneNumberWithExtension,
  getCountryCode,
  transformPhoneForAPI,
  trimSpace,
} from '../../utils/HelperMethods';
import {fetchApi} from '../../utils/ApiHelper';
import {getData, storeData} from '../../utils/GeneralStorage';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import UI from './ui';
import {trackEvent} from '../../utils/Analytics';
import {RequestFailedIcon} from '../../utils/SvgIcons';

export const ErrorBottomExpandableDrawer = ({
  visible = false,
  onClose = () => {},
}) => {
  const {colors} = useTheme();
  return (
    <BottomExpandableDrawer
      title={'Request Failed'}
      rippleColor={colors.dangerMuted}
      visible={visible}
      showRipple={visible}
      subtitle={'Your submission failed to send. Please try again.'}
      rippleIcon={<RequestFailedIcon />}
      buttons={[
        {
          title: 'Close',
          buttonType: BUTTON_TYPES.Bordered,
          onPress: onClose,
        },
      ]}
    />
  );
};

export const getOTP = (
  phoneNumber,
  navigation,
  setBottomModalVisible,
  lpc,
  esc,
  moveToNextStep = () => {},
  fromLogin,
  isSourceApp,
  authCredentials,
) => {
  const postData = {
    phone: transformPhoneForAPI(phoneNumber),
    type: SEND_OTP_TYPES.new,
  };
  lpc.setVisible(true);
  fetchApi(HTTP_METHODS.POST, SEND_OTP, postData)
    .then(response => {
      lpc.setVisible(false);
      if (response.status == 200) {
        if (Platform.OS === 'web') {
          // forwarding to phone verification step
          if (fromLogin) {
            navigation('/sign-up', {
              state: {step: 3, isSourceApp, authCredentials},
            });
          } else {
            moveToNextStep(3);
          }
        } else {
          navigation.navigate('PhoneVerificationSignup', {isSourceApp});
        }
      } else {
        //if OTP call fails due to any reason including an invalid phone number
        setTimeout(() => {
          setBottomModalVisible(true);
        }, 1000);
      }
    })
    .catch(error => {
      lpc.setVisible(false);
      //This needs to be handled gracefully as discussed earlier
      //TODO: Link the handler once created by darshit
      //if OTP call fails due to any reason including an invalid phone number
      // setBottomModalVisible(true);
      esc.showError(error);
    });
};

const AccountDetailsScreen = ({navigation, ...props}) => {
  //storeData(KEYS.numAttemptKey,'0')

  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [repeatPasswordError, setRepeatPasswordError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);
  const [checkboxSelected, setCheckboxSelected] = useState(false);
  const [isValidForm, setIsValidForm] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState(false);
  const [bottomModalVisible, setBottomModalVisible] = useState(false);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  const onPressRequestFailed = () => {
    setBottomModalVisible(false);
  };

  const onChangePhoneNumber = phone => {
    setPhoneNumber(phone);
  };

  const onPressCheckbox = () => {
    setCheckboxSelected(!checkboxSelected);
  };

  const onPressShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onPressShowRepeatPassword = () => {
    setShowRepeatPassword(!showRepeatPassword);
  };

  const navigateToTermsOfService = () => {
    if (Platform.OS === 'web') {
      window.open(TERMS_AND_CONDITIONS_URL, '_blank');
    } else {
      navigation.navigate('DiathriveTermsOfService');
    }
  };

  const navigateToPrivacyPolicy = () => {
    if (Platform.OS === 'web') {
      window.open(PRIVACY_POLICY_URL, '_blank');
    } else {
      navigation.navigate('DiathrivePrivacyPolicy');
    }
  };

  useEffect(() => {
    validateSignin();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validation intentionally keyed to checkbox change only
  }, [checkboxSelected]);

  const validFirstName = () => {
    if (!firstName) {
      setIsValidForm(false);
      setFirstNameError(true);
      return false;
    } else {
      setFirstNameError(false);
      validateSignin();
    }
  };

  const validLastName = () => {
    if (!lastName) {
      setIsValidForm(false);
      setLastNameError(true);
      return false;
    } else {
      setLastNameError(false);
      validateSignin();
    }
  };

  const validEmail = () => {
    if (email && !validateEmail(trimSpace(email))) {
      setIsValidForm(false);
      setEmailError(true);
      return false;
    } else {
      setEmailError(false);
      validateSignin();
    }
  };

  const validedPassword = () => {
    if (password && !validPassword(password)) {
      setIsValidForm(false);
      setPasswordError(true);
      return false;
    } else {
      setPasswordError(false);
      validateSignin();
    }
  };

  const validRepeatPassword = () => {
    if (repeatPassword && password !== repeatPassword) {
      setIsValidForm(false);
      setRepeatPasswordError(true);
      return false;
    } else {
      setRepeatPasswordError(false);
      validateSignin();
    }
  };

  const validPhoneNumber = () => {
    const phone = getCountryCode() + phoneNumber;
    if (phone.length > 2 && !validPhoneNumberWithExtension(phone)) {
      setIsValidForm(false);
      setPhoneNumberError(true);
      return false;
    } else {
      setPhoneNumberError(false);
      validateSignin();
    }
  };

  const validateSignin = () => {
    const phone = getCountryCode() + phoneNumber;
    if (
      isEmpty(firstName) ||
      (email && !validateEmail(trimSpace(email))) ||
      (password && !validPassword(password)) ||
      (repeatPassword && password !== repeatPassword) ||
      (phone.length > 2 && !validPhoneNumberWithExtension(phone)) ||
      isEmpty(email) ||
      isEmpty(password) ||
      isEmpty(repeatPassword) ||
      phoneNumber.length <= 2 ||
      !checkboxSelected
    ) {
      setIsValidForm(false);
      return;
    }

    setIsValidForm(true);
  };

  const saveDetails = () => {
    getData(KEYS.employerUniqueId).then(value => saveAccountDetails(value));
  };

  const saveAccountDetails = async employerId => {
    await storeData(KEYS.phoneNumber, phoneNumber);
    if (Platform.OS === 'web') {
      props.setAuthCredentials({
        username: trimSpace(email).toLowerCase(),
        password: password,
      });
    } else {
      await Keychain.setGenericPassword(
        trimSpace(email).toLowerCase(),
        password,
      );
    }

    let phone = transformPhoneForAPI(phoneNumber);
    lpc.setVisible(true);
    fetchApi(HTTP_METHODS.POST, CREATE_USER, {
      employerId: trimSpace(employerId),
      firstname: trimSpace(firstName),
      lastname: trimSpace(lastName),
      email: trimSpace(email).toLowerCase(),
      phone,
      password,
    })
      .then(response => response.data)
      .then(data => {
        lpc.setVisible(false);
        if (data.status == 'success') {
          trackEvent('Create Account');
          getOTP(
            phoneNumber,
            navigation,
            setBottomModalVisible,
            lpc,
            esc,
            props?.moveToNextStep,
            false,
            true,
          );
        } else {
          esc.showError('Your submission failed to send. Please try again.');
          // setTimeout(() => {
          //   setBottomModalVisible(true);
          // }, 1000);
        }
      })
      .catch(error => {
        lpc.setVisible(false);
        esc.showError(error);
        //possibly save error in analytics too
        // setTimeout(() => {
        //   setBottomModalVisible(true);
        // }, 1000);
      });
  };

  const uiProps = {
    navigation,
    bottomModalVisible,
    onPressRequestFailed,
    firstName,
    setFirstName,
    validFirstName,
    firstNameError,
    lastName,
    setLastName,
    validLastName,
    lastNameError,
    email,
    setEmail,
    validEmail,
    emailError,
    password,
    setPassword,
    validedPassword,
    passwordError,
    showPassword,
    onPressShowPassword,
    repeatPassword,
    setRepeatPassword,
    repeatPasswordError,
    validRepeatPassword,
    showRepeatPassword,
    onPressShowRepeatPassword,
    phoneNumber,
    onChangePhoneNumber,
    validPhoneNumber,
    phoneNumberError,
    navigateToTermsOfService,
    navigateToPrivacyPolicy,
    checkboxSelected,
    onPressCheckbox,
    isValidForm,
    saveDetails,
    ...props,
  };

  return <UI {...uiProps} />;
};

export default AccountDetailsScreen;
